/**
 * Campaign Delete Dialog Component
 * 
 * Specialized confirmation dialog for campaign deletion with detailed information display.
 * Shows campaign details and consequences of deletion in a clear, user-friendly format.
 * 
 * @author AI Assistant
 * @created 2024
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Article as PostIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 400,
    maxWidth: 520,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      maxWidth: '95vw',
      margin: theme.spacing(1)
    }
  }
}));

const CampaignNameBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2)
}));

const DeleteItemsList = styled(List)(({ theme }) => ({
  backgroundColor: theme.palette.error.light + '10',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 0),
  marginBottom: theme.spacing(2)
}));

const CampaignDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  campaign,
  loading = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!campaign) return null;

  const handleConfirm = () => {
    if (!loading) {
      onConfirm(campaign);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const deleteItems = [
    {
      icon: <CampaignIcon />,
      primary: 'The campaign and all its settings',
      secondary: 'Campaign configuration, goals, and metadata'
    },
    {
      icon: <PostIcon />,
      primary: `${campaign.totalPosts || 0} associated posts`,
      secondary: 'All posts, drafts, and scheduled content'
    },
    {
      icon: <AnalyticsIcon />,
      primary: 'All campaign analytics and history',
      secondary: 'Performance data, insights, and reports'
    },
    {
      icon: <SettingsIcon />,
      primary: 'Campaign configurations',
      secondary: 'Sequences, templates, and custom settings'
    }
  ];

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="campaign-delete-dialog-title"
      aria-describedby="campaign-delete-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        id="campaign-delete-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6" component="span">
            Delete Campaign
          </Typography>
        </Box>
        <Button
          onClick={handleClose}
          size="small"
          sx={{ minWidth: 'auto', p: 0.5 }}
          disabled={loading}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" gutterBottom id="campaign-delete-dialog-description">
          Are you sure you want to delete this campaign?
        </Typography>

        {/* Campaign Information */}
        <CampaignNameBox>
          <CampaignIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="600">
              {campaign.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {campaign.websiteUrl}
            </Typography>
          </Box>
        </CampaignNameBox>

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
          This will permanently delete:
        </Typography>

        {/* What will be deleted */}
        <DeleteItemsList dense>
          {deleteItems.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="500">
                    {item.primary}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {item.secondary}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </DeleteItemsList>

        {/* Warning Alert */}
        <Alert 
          severity="error" 
          variant="outlined"
          sx={{ 
            border: '1px solid',
            borderColor: 'error.main',
            backgroundColor: 'error.light + 05'
          }}
        >
          <Typography variant="body2" fontWeight="500">
            This action cannot be undone.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            All data associated with this campaign will be permanently lost.
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
          variant="outlined"
          size={isMobile ? 'medium' : 'large'}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          color="error"
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <WarningIcon />}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Deleting...' : 'Delete Campaign'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default CampaignDeleteDialog; 